import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setToken, getToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(false);
      return null;
    }
    try {
      const { data } = await api.get("/auth/me");
      // Fetch knowledge_base_filled flag once so the navbar can render the
      // "incomplete" badge without a second request on every page.
      let kbFilled = false;
      try {
        const kb = await api.get("/knowledge-base");
        kbFilled = !!kb.data?.is_filled;
      } catch {
        // ignore — if KB endpoint fails (e.g. suspended user), the badge
        // simply doesn't render. Suspended path handles itself.
      }
      const merged = { ...data, knowledge_base_filled: kbFilled };
      setUser(merged);
      return merged;
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403 || !getToken()) {
        setToken(null);
        setUser(false);
        return null;
      }
      // Transient failure (network / 5xx): keep an existing session as is;
      // on first load fall back to the login page instead of spinning forever.
      setUser((prev) => (prev === null ? false : prev));
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.access_token) setToken(data.access_token);
    setUser(data);
    return data;
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    if (data.access_token) setToken(data.access_token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore
    }
    setToken(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
